import { gql } from '@apollo/client'

export interface IPrepItemCategory {
  prepItemIdCode: string
  prepItemName: string
  parentPrepCategoryIdCode: string
}

export interface IPrepItemCategoryType {
  getPrepItemCategory: {
    nodes: IPrepItemCategory[]
  }
}

export const getPrepItemCategory = gql`
  query GetPrepItemCategory {
    getPrepItemCategory {
      nodes {
        prepItemIdCode
        prepItemName
        parentPrepCategoryIdCode
      }
    }
  }
`

export interface IPrepItemBatchStandard {
  prepItemIdCode: string
  prepItemName: string
  prepBatchType: string
  batchTypeId: number
  unit: string
  unitId: number
  standardUnitsPerBatch: number
  sourceType: 'PREP' | 'ONHAND'
}

export interface IPrepItemBatchStandardsType {
  getPrepItemBatchStandards: {
    nodes: IPrepItemBatchStandard[]
  }
}

export const getPrepItemBatchStandards = gql`
  query GetPrepItemBatchStandards {
    getPrepItemBatchStandards {
      nodes {
        sourceType
        prepItemIdCode
        prepItemName
        prepBatchType
        batchTypeId
        unit
        unitId
        standardUnitsPerBatch
      }
    }
  }
`

export interface IPrepItemSettings {
  prepItemIdCode: string
  displayOnHandBatchTypeId: number
  displayOnHandSourceType: string
  displayOnHandBatchType: string
  restDays: number
  minimumUnit: number
}

export interface IPrepItemSettingsType {
  getPrepItemSettings: {
    nodes: IPrepItemSettings[]
  }
}

export const getPrepItemSettings = gql`
  query GetPrepItemSettings {
    getPrepItemSettings {
      nodes {
        prepItemIdCode
        displayOnHandBatchTypeId
        displayOnHandSourceType
        displayOnHandBatchType
        restDays
        minimumUnit
      }
    }
  }
`

export interface IPrepItemBatchType {
  batchTypeId: number
  batchTypeName: string
  unit: string
  sourceType: 'PREP' | 'ONHAND'
}

export interface IPrepItemBatchTypesType {
  getPrepItemBatchTypes: {
    nodes: IPrepItemBatchType[]
  }
}

export const getPrepItemBatchTypes = gql`
  query GetPrepItemBatchTypes {
    getPrepItemBatchTypes {
      nodes {
        batchTypeId
        batchTypeName
        unit
        sourceType
      }
    }
  }
`

export interface IEditPrepItemPrepBatchStandardVariables {
  iPrepItemId: string
  iBatchTypeId: number
  iStandardUnitsPerBatch: number
}

export const editPrepItemPrepBatchStandard = gql`
  mutation EditPrepItemPrepBatchStandard(
    $iPrepItemId: String!
    $iBatchTypeId: Int!
    $iStandardUnitsPerBatch: Float!
  ) {
    editPrepItemPrepBatchStandard(
      input: {
        iPrepItemId: $iPrepItemId
        iBatchTypeId: $iBatchTypeId
        iStandardUnitsPerBatch: $iStandardUnitsPerBatch
      }
    ) {
      clientMutationId
    }
  }
`

export interface IBatchInput {
  batch_type_id: number
  standard_units_per_batch: number
  display_order: number
}

export interface IEditPrepItemOnHandBatchStandardVariables {
  iPrepItemId: string
  iBatches: IBatchInput[]
}

export const editPrepItemOnHandBatchStandard = gql`
  mutation EditPrepItemOnHandBatchStandard(
    $iPrepItemId: String!
    $iBatches: JSON!
  ) {
    editPrepItemOnHandBatchStandard(
      input: { iPrepItemId: $iPrepItemId, iBatches: $iBatches }
    ) {
      clientMutationId
    }
  }
`

// Note: The edit_prep_item_onHand_batch_standard function handles deletions
// by marking existing records as deleted before inserting new ones.
// So we don't need a separate delete mutation.

export interface IPrepItemPrepWeekday {
  prepItemIdCode: string
  prepItemName: string
  sunday: boolean
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
}

export interface IPrepItemPrepWeekdayType {
  getPrepItemPrepWeekday: {
    nodes: IPrepItemPrepWeekday[]
  }
}

export interface IWeekdayInput {
  prep_item_id: string
  schedule_name: string
  sunday: boolean
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  updated_at?: string
}

export interface IPrepItemWithAmountPerSoldItem {
  prepItemIdCode: string
  prepItemName: string
  menuItemId: number
  menuItemName: string
  unitId: number
  unit: string
  amountNeededPerSoldItem: number
}

export const getPrepItemsWithAmountPerSoldItem = gql`
  query GetPrepItemsWithAmountPerSoldItem {
    getPrepItemsWithAmountPerSoldItem {
      nodes {
        prepItemIdCode
        prepItemName
        menuItemId
        menuItemName
        unitId
        unit
        amountNeededPerSoldItem
      }
    }
  }
`

export interface IMenuItemMappingInput {
  menu_item_id: number
  unit_id: number
  amount_needed_per_sold_item: number
}

export interface IEditPrepItemMenuItemMappingVariables {
  iPrepItemId: string
  iMappingDetails: IMenuItemMappingInput[]
}

export const editPrepItemMenuItemMapping = gql`
  mutation EditPrepItemMenuItemMapping(
    $iPrepItemId: String!
    $iMappingDetails: JSON!
  ) {
    editPrepItemMenuItemMapping(
      input: { iPrepItemId: $iPrepItemId, iMappingDetails: $iMappingDetails }
    ) {
      clientMutationId
    }
  }
`

export interface IMenuItem {
  itemId: number
  itemType: string
  itemDisplayName: string
}

export interface IMenuItemsType {
  listItems: {
    nodes: IMenuItem[]
  }
}

export const getMenuItems = gql`
  query ListItems {
    listItems {
      nodes {
        itemId
        itemType
        itemDisplayName
      }
    }
  }
`

export interface IUnit {
  unitId: number
  unitName: string
}

export interface IUnitsType {
  getPrepItemUnits: {
    nodes: IUnit[]
  }
}

export const getUnits = gql`
  query GetUnits {
    getPrepItemUnits {
      nodes {
        unitId
        unitName
      }
    }
  }
`
export interface IEditPrepItemNameVariables {
  iPrepItemId: string
  iName: string
}

export const editPrepItemName = gql`
  mutation EditPrepItemName($iPrepItemId: String!, $iName: String!) {
    editPrepItemName(input: { iPrepItemId: $iPrepItemId, iName: $iName }) {
      clientMutationId
    }
  }
`

export interface IUpdatePrepItemRestDaysVariables {
  prepItemId: string
  restDays: number
}

export const updatePrepItemRestDays = gql`
  mutation UpdatePrepItemRestDays($prepItemId: String!, $restDays: Int!) {
    updatePrepItemSetting(
      input: { prepItemId: $prepItemId, patch: { restDays: $restDays } }
    ) {
      prepItemSetting {
        prepItemId
        restDays
      }
    }
  }
`

export const updatePrepItemDisplayOnHandBatchTypeId = gql`
  mutation UpdatePrepItemDisplayOnHandBatchTypeId(
    $prepItemId: String!
    $displayOnHandBatchTypeId: Int
  ) {
    updatePrepItemSetting(
      input: {
        prepItemId: $prepItemId
        patch: { displayOnHandBatchTypeId: $displayOnHandBatchTypeId }
      }
    ) {
      prepItemSetting {
        prepItemId
        displayOnHandBatchTypeId
      }
    }
  }
`

// Delivery Day Scheduling — config_2 table (per-store support)
export interface IPrepItemWeekdayConfig2 {
  prepItemIdCode: string
  prepItemName: string
  scheduleName: string
  isActivated: boolean
  sunday: boolean
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
}

export interface IPrepItemWeekdayConfig2Type {
  getPrepItemWeekdayConfig2: {
    nodes: IPrepItemWeekdayConfig2[]
  }
}

export const getPrepItemWeekdayConfig2 = gql`
  query GetPrepItemWeekdayConfig2($iQueryDate: Date!, $iLocationId: Int) {
    getPrepItemWeekdayConfig2(
      iQueryDate: $iQueryDate
      iLocationId: $iLocationId
    ) {
      nodes {
        prepItemIdCode
        prepItemName
        scheduleName
        isActivated
        sunday
        monday
        tuesday
        wednesday
        thursday
        friday
        saturday
      }
    }
  }
`

export interface IEditPrepItemWeekdayConfig2Variables {
  iScheduleId: number
  iLocationId: number
  iWeekdays: IWeekdayInput[]
}

// why: edit_prep_item_weekday_config_2 SQL function was re-keyed from
// schedule_name (TEXT) to schedule_id (INTEGER) as part of the v2 schedule
// registry refactor. Callers now look up the active prep_base schedule_id
// via getPrepItemSchedules2 (or its inline equivalent) before saving.
export const editPrepItemWeekdayConfig2 = gql`
  mutation EditPrepItemWeekdayConfig2(
    $iScheduleId: Int!
    $iLocationId: Int
    $iWeekdays: JSON!
  ) {
    editPrepItemWeekdayConfig2(
      input: {
        iScheduleId: $iScheduleId
        iLocationId: $iLocationId
        iWeekdays: $iWeekdays
      }
    ) {
      clientMutationId
    }
  }
`

// why: the v2 schedule registry. Today there's exactly one row (the seeded
// 'Standard' default); callers fetch this when they need a schedule_id to
// pass into v2 mutations. Future timed prep bases (Holiday Week, etc.) land
// here as additional rows with start_date / end_date set — see the backlog
// ticket "UI for creating timed prep-base schedules".
export interface IPrepItemScheduleConfig2 {
  id: number
  name: string
  isDefault: boolean
  startDate: string | null
  endDate: string | null
  deletedAt: string | null
}

export interface IPrepItemScheduleConfig2Type {
  prepItemScheduleConfig2s: {
    nodes: IPrepItemScheduleConfig2[]
  }
}

export const getPrepItemScheduleConfig2 = gql`
  query GetPrepItemScheduleConfig2 {
    prepItemScheduleConfig2s {
      nodes {
        id
        name
        isDefault
        startDate
        endDate
        deletedAt
      }
    }
  }
`

export interface IDeliveryScheduleLocation {
  locationId: number
  locationName: string
  locationCode: string
  deliveryDays: string
}

export interface IDeliveryScheduleLocationsType {
  getDeliveryScheduleLocations: {
    nodes: IDeliveryScheduleLocation[]
  }
}

export const getDeliveryScheduleLocations = gql`
  query GetDeliveryScheduleLocations {
    getDeliveryScheduleLocations {
      nodes {
        locationId
        locationName
        locationCode
        deliveryDays
      }
    }
  }
`
